// Client-side exporters for the AI Writer v2 Publish step — the 7 download
// formats. Reuses JSZip + file-saver (already project deps), the legacy
// helper.js HTML utilities, and the existing /api/download/ai_writer/docx
// endpoint for both .docx and .docx-with-highlighted-keywords.
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { beautifyHtmlString, downloadAsHtml } from "@/components/FullPageSourceEditorV2/components/AiWriter/helper";
import { downloadDocxForAiWriter } from "@/api";
import { outlineHtmlFromRows } from "../../utils/outline";

function triggerTextDownload(filename, text, mime = "text/plain") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function esc(s) {
  return String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// The server-assembled `fullHtml` carries the SEO <title> but no page <h1> (the
// Publish preview renders the H1 separately). Inject it at the top of <body> so
// every download includes the page title, matching what the user sees.
function withPageTitle(fullHtml, pageTitle) {
  if (!pageTitle) return fullHtml;
  const doc = new DOMParser().parseFromString(fullHtml || "", "text/html");
  const body = doc.body;
  if (!body) return fullHtml;
  const first = body.firstElementChild;
  if (!(first && first.tagName === "H1")) {
    const h1 = doc.createElement("h1");
    h1.textContent = pageTitle;
    body.insertBefore(h1, body.firstChild);
  }
  return "<!DOCTYPE html>\n" + (doc.documentElement ? doc.documentElement.outerHTML : fullHtml);
}

function collapse(s) {
  return String(s || "").replace(/\s+/g, " ").trim();
}

// Render a <table> as an aligned plain-text grid (fixes the collapsed innerText
// tables): columns padded to their max width, with a separator after the header.
function tableToText(table) {
  const grid = Array.from(table.querySelectorAll("tr")).map((tr) =>
    Array.from(tr.querySelectorAll("th, td")).map((c) => collapse(c.textContent))
  );
  if (!grid.length) return [];
  const cols = Math.max(...grid.map((r) => r.length));
  const widths = [];
  for (let c = 0; c < cols; c++) {
    widths[c] = Math.max(1, ...grid.map((r) => (r[c] || "").length));
  }
  const fmt = (r) => {
    const cells = [];
    for (let c = 0; c < cols; c++) cells.push((r[c] || "").padEnd(widths[c]));
    return cells.join("  |  ").replace(/\s+$/, "");
  };
  const out = [];
  grid.forEach((r, i) => {
    out.push(fmt(r));
    if (i === 0) out.push(widths.map((w) => "-".repeat(w)).join("--+--"));
  });
  return out;
}

// HTML -> plain text with real block breaks. `innerText` on a DOMParser document
// is never laid out, so it collapses toward `textContent` (no line breaks); walk
// the DOM ourselves to break after every heading/paragraph and format tables.
function htmlToPlainText(html, pageTitle) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  const out = [];
  if (pageTitle) out.push(pageTitle, "");
  const emit = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType !== 1) return;
      const tag = child.tagName.toLowerCase();
      if (/^h[1-6]$/.test(tag) || tag === "p" || tag === "blockquote") {
        const t = collapse(child.textContent);
        if (t) out.push(t, "");
      } else if (tag === "ul" || tag === "ol") {
        Array.from(child.children)
          .filter((li) => li.tagName === "LI")
          .forEach((li, i) => {
            const t = collapse(li.textContent);
            if (t) out.push((tag === "ol" ? i + 1 + ". " : "- ") + t);
          });
        out.push("");
      } else if (tag === "table") {
        tableToText(child).forEach((l) => out.push(l));
        out.push("");
      } else if (tag === "img" || tag === "figure" || tag === "hr") {
        /* skip */
      } else {
        emit(child); // recurse wrappers (div / section / article)
      }
    });
  };
  emit(doc.body);
  return out.join("\n").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
}

// Wrap occurrences of each term in the HTML body (DOM-safe, text-nodes only).
function highlightHtml(html, terms, wrapHtml) {
  if (!terms || !terms.length) return html;
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  const escaped = terms
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .sort((a, b) => b.length - a.length);
  if (!escaped.length) return html;
  const re = new RegExp("\\b(" + escaped.join("|") + ")\\b", "gi");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null, false);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    if (!re.test(node.nodeValue)) return;
    re.lastIndex = 0;
    // Replace the text node with a FRAGMENT (no wrapper element): an injected
    // <span> wrapper picks up global span styling and shrinks the surrounding
    // heading/paragraph text whenever a keyword matches inside it.
    const tpl = doc.createElement("template");
    tpl.innerHTML = node.nodeValue.replace(re, (m) => wrapHtml(m));
    node.parentNode.replaceChild(tpl.content, node);
  });
  return doc.body.innerHTML;
}

async function buildZip(fullHtml, filename) {
  const zip = new JSZip();
  const doc = new DOMParser().parseFromString(fullHtml || "", "text/html");
  const imgs = Array.from(doc.querySelectorAll("img"));
  const folder = zip.folder("images");
  for (const img of imgs) {
    const src = img.getAttribute("src") || "";
    const name = src.split("/").pop();
    if (!name) continue;
    try {
      const resp = await fetch(src);
      const blob = await resp.blob();
      folder.file(name, blob);
      img.setAttribute("src", "images/" + name);
    } catch (e) { /* skip unfetchable image */ }
  }
  const html = "<!DOCTYPE html>\n" + (doc.documentElement ? doc.documentElement.outerHTML : fullHtml);
  zip.file("index.html", beautifyHtmlString(html));
  const content = await zip.generateAsync({ type: "blob" });
  saveAs(content, filename);
}

async function docx(token, html, filename) {
  const res = await downloadDocxForAiWriter(token, { htmlStr: html });
  saveAs(new Blob([res.data]), filename);
}

// format -> exporter. ctx = { token, fullHtml, terms, rows, baseName, pageTitle, seoTitle }
export async function runExport(format, ctx) {
  const { token, fullHtml, terms, rows, baseName, pageTitle, seoTitle } = ctx;
  const titledHtml = withPageTitle(fullHtml, pageTitle);
  switch (format) {
    case "plain_txt":
      return triggerTextDownload(baseName + ".txt", htmlToPlainText(fullHtml, pageTitle));
    case "html_txt":
      return triggerTextDownload(baseName + ".html.txt", beautifyHtmlString(titledHtml));
    case "html":
      return downloadAsHtml(baseName + ".html", titledHtml);
    case "zip":
      return buildZip(titledHtml, baseName + ".zip");
    case "docx":
      return docx(token, titledHtml, baseName + ".docx");
    case "docx_highlighted": {
      const highlighted = highlightHtml(titledHtml, terms, (m) => `<span style="background-color: yellow;">${m}</span>`);
      return docx(token, highlighted, baseName + "-highlighted.docx");
    }
    case "outline": {
      const inner = (pageTitle ? `<h1>${esc(pageTitle)}</h1>\n` : "") + outlineHtmlFromRows(rows);
      const docHtml =
        `<!DOCTYPE html>\n<html>\n<head>\n<meta charset="utf-8">\n<title>${esc(seoTitle || pageTitle)}</title>\n</head>\n<body>\n` +
        inner +
        `\n</body>\n</html>`;
      return downloadAsHtml(baseName + "-outline.html", docHtml);
    }
    default:
      return null;
  }
}

export { highlightHtml, withPageTitle };
